import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUser,
    faBriefcase,
    faShieldHeart,
    faCamera,
    faUpload,
    faTimes,
    faAngleLeft,
    faAngleRight,
    faSpinner,
    faInfoCircle,
    faFileAlt
} from "@fortawesome/free-solid-svg-icons";
import PageHeadingSection from "../../../../common/pageheading/PageHeadingSection";
import CustomSelect from "../../../../common/formfields/CustomSelect";
import Breadcrumbs from "../../../../common/BreadCrum/BreadCrum";
import staffService from "../../../../../services/staffService";
import { uploadImageToAzure } from "../../../../../services/AzureStorageService";
import storageService from "../../../../../utils/storageService";

// Helper for consistent ISO date conversion without local timezone shift
const toIsoDateString = (dateStr) => {
    if (!dateStr) return null;
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return null;
        return d.toISOString();
    } catch {
        return null;
    }
};

// Helper for calculating age accurately from Date of Birth
const calculateAge = (dobString) => {
    if (!dobString) return "";
    try {
        const birthDate = new Date(dobString);
        if (isNaN(birthDate.getTime())) return "";
        const today = new Date();
        let calculatedAge = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            calculatedAge--;
        }
        return calculatedAge >= 0 ? String(calculatedAge) : "";
    } catch {
        return "";
    }
};

const StaffForm = ({ initialVendorId = null, initialVendorName = null }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user: authUser } = useSelector((state) => state.auth);

    const staffDataFromState = location.state?.staffData;
    const editStaffId = staffDataFromState?.staffId || staffDataFromState?.id;
    const isEditMode = Boolean(editStaffId);

    // Extract query params if available
    const searchParams = new URLSearchParams(location.search);
    const queryVendorId = searchParams.get("vendorId") || initialVendorId || (staffDataFromState?.vendorId ? String(staffDataFromState.vendorId) : "");
    const queryVendorName = searchParams.get("vendorName") || initialVendorName || staffDataFromState?.vendorName || "";

    // Logged in user context
    const assignedSiteId = authUser?.siteId || authUser?.SiteId || Number(storageService.getItem("siteId")) || 2;
    const assignedWingId = authUser?.wingId || authUser?.WingId || 1;
    const assignedFloorId = authUser?.floorId || authUser?.FloorId || 1;
    const assignedUnitId = authUser?.unitId || authUser?.UnitId || Number(storageService.getItem("unitId")) || 2;
    const createdBy = authUser?.userName || authUser?.name || storageService.getItem("employeeName") || "AdminUser";

    // Staff Type is determined directly by the entry point:
    // - From Vendor module (queryVendorId present) -> 'vendor'
    // - From Workforce Staff module -> 'vendorless'
    const staffType = queryVendorId ? "vendor" : "vendorless";

    const [formData, setFormData] = useState({
        // Personal Information
        userImage: "",
        firstName: "",
        lastName: "",
        mobileNumber: "",
        gender: "",
        dateOfBirth: "",
        age: "",
        bloodGroup: "",

        // Employment & Duty Details
        vendorId: queryVendorId || "",
        vendorName: queryVendorName || "",
        staffRoleId: "",
        joiningDate: new Date().toISOString().split("T")[0],
        employmentStatus: "Active",

        // Duty details for Vendorless staff
        shiftId: "",

        // Emergency & Document Info
        emergencyContactName: "",
        emergencyContactNumber: "",
        documentType: "Aadhaar",
        documentNumber: "",
        documentFilePath: "",
        documentFileName: ""
    });

    // Populate data when editing an existing staff member
    useEffect(() => {
        if (staffDataFromState) {
            const s = staffDataFromState;
            const primaryDoc = s.document || (Array.isArray(s.documents) && s.documents.length > 0 ? s.documents[0] : null);
            const primaryAllotment = s.allotment || (Array.isArray(s.allotments) && s.allotments.length > 0 ? s.allotments[0] : null);

            let docType = primaryDoc?.documentType || "Aadhaar";
            const docLower = docType.toLowerCase();
            if (docLower.includes("aadhaar")) docType = "Aadhaar";
            else if (docLower.includes("pan")) docType = "PAN";
            else if (docLower.includes("voter")) docType = "Voter ID";
            else if (docLower.includes("driving")) docType = "Driving License";
            else if (docLower.includes("passport")) docType = "Passport";

            const rawDob = s.dateOfBirth ? s.dateOfBirth.split("T")[0] : "";
            const computedAge = s.age ? String(s.age) : calculateAge(rawDob);

            setFormData((prev) => ({
                ...prev,
                userImage: s.photoUrl || s.userImage || "",
                firstName: s.firstName || "",
                lastName: s.lastName || "",
                mobileNumber: s.mobileNumber || "",
                gender: s.gender || "",
                dateOfBirth: rawDob,
                age: computedAge,
                bloodGroup: s.bloodGroup || "",
                vendorId: s.vendorId ? String(s.vendorId) : (queryVendorId || ""),
                vendorName: s.vendorName || queryVendorName || "",
                staffRoleId: s.staffRoleId ? String(s.staffRoleId) : "",
                joiningDate: s.joiningDate ? s.joiningDate.split("T")[0] : new Date().toISOString().split("T")[0],
                employmentStatus: s.employmentStatus || "Active",
                shiftId: primaryAllotment?.shiftId ? String(primaryAllotment.shiftId) : prev.shiftId,
                emergencyContactName: s.emergencyContactName || "",
                emergencyContactNumber: s.emergencyContactNumber || "",
                documentType: docType,
                documentNumber: primaryDoc?.documentNumber || "",
                documentFilePath: primaryDoc?.documentUrl || primaryDoc?.filePath || "",
                documentFileName: primaryDoc?.documentFileName || (primaryDoc?.documentUrl ? (primaryDoc?.documentType || "Uploaded Document") : "")
            }));
        }
    }, [staffDataFromState, queryVendorId, queryVendorName]);

    const [errors, setErrors] = useState({});
    const [roles, setRoles] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [shifts, setShifts] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(false);
    const [loadingVendors, setLoadingVendors] = useState(false);
    const [loadingShifts, setLoadingShifts] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [uploadingDoc, setUploadingDoc] = useState(false);

    // Webcam states
    const [showWebcam, setShowWebcam] = useState(false);
    const videoRef = useRef(null);
    const streamRef = useRef(null);

    // Fetch dynamic staff roles based on siteId
    useEffect(() => {
        const fetchRoles = async () => {
            setLoadingRoles(true);
            try {
                const response = await staffService.getStaffRoles(assignedSiteId);
                if (response.data?.isSuccess && Array.isArray(response.data?.data)) {
                    const activeRoles = response.data.data
                        .filter((r) => r.is_active !== false)
                        .map((r) => ({
                            label: r.roleName,
                            value: String(r.staffRoleId)
                        }));
                    setRoles(activeRoles);
                } else {
                    setRoles([]);
                }
            } catch (err) {
                console.error("Failed to load staff roles:", err);
                toast.error("Could not load roles for this site");
                setRoles([]);
            } finally {
                setLoadingRoles(false);
            }
        };

        fetchRoles();
    }, [assignedSiteId]);

    // Fetch shifts for staff (personTypeId = 3)
    useEffect(() => {
        const fetchShiftsList = async () => {
            setLoadingShifts(true);
            try {
                const response = await staffService.getShifts(3);
                if (response.data?.isSuccess && Array.isArray(response.data?.data)) {
                    const shiftOpts = response.data.data
                        .filter((s) => s.isActiveId === 1 || s.is_active !== false)
                        .map((s) => ({
                            label: s.description || `${s.shiftName} (${s.startTime} - ${s.endTime})`,
                            value: String(s.id)
                        }));
                    setShifts(shiftOpts);
                    if (shiftOpts.length > 0) {
                        setFormData((prev) => ({
                            ...prev,
                            shiftId: prev.shiftId || shiftOpts[0].value
                        }));
                    }
                } else {
                    setShifts([]);
                }
            } catch (err) {
                console.error("Failed to load staff shifts:", err);
                toast.error("Could not load shifts");
                setShifts([]);
            } finally {
                setLoadingShifts(false);
            }
        };

        if (staffType === "vendorless") {
            fetchShiftsList();
        }
    }, [staffType]);

    // Fetch vendors list for Flow A
    useEffect(() => {
        const fetchVendorsList = async () => {
            setLoadingVendors(true);
            try {
                const response = await staffService.getVendors({
                    siteId: assignedSiteId,
                    pageNumber: 1,
                    pageSize: 100
                });
                if (response.data?.isSuccess && Array.isArray(response.data?.data)) {
                    const vendorOpts = response.data.data.map((v) => ({
                        label: v.vendorName,
                        value: String(v.vendorId)
                    }));
                    setVendors(vendorOpts);
                }
            } catch (err) {
                console.error("Failed to load vendors:", err);
            } finally {
                setLoadingVendors(false);
            }
        };

        if (staffType === "vendor" && !queryVendorId) {
            fetchVendorsList();
        }
    }, [staffType, queryVendorId, assignedSiteId]);

    // Input change handler with automatic age calculation
    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData((prev) => {
            const updated = { ...prev, [id]: value };

            if (id === "dateOfBirth") {
                updated.age = calculateAge(value);
            }

            return updated;
        });

        if (errors[id]) {
            setErrors((prev) => ({ ...prev, [id]: null }));
        }
    };

    // Photo Handling via Azure Storage
    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            toast.error("Please select a valid image file");
            return;
        }

        setUploadingPhoto(true);
        try {
            const uploadedUrl = await uploadImageToAzure(file);
            if (uploadedUrl) {
                setFormData((prev) => ({ ...prev, userImage: uploadedUrl }));
                toast.success("Photo uploaded successfully!");
            } else {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setFormData((prev) => ({ ...prev, userImage: reader.result }));
                };
                reader.readAsDataURL(file);
            }
            if (errors.userImage) setErrors((prev) => ({ ...prev, userImage: null }));
        } catch (err) {
            console.error("Image upload failed:", err);
            toast.error("Failed to upload photo");
        } finally {
            setUploadingPhoto(false);
        }
    };

    // Live Webcam
    const startWebcam = async () => {
        setShowWebcam(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            streamRef.current = stream;
            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                }
            }, 100);
        } catch (err) {
            console.error("Error accessing webcam:", err);
            toast.error("Could not access webcam. Please check permissions.");
            setShowWebcam(false);
        }
    };

    const stopWebcam = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        setShowWebcam(false);
    };

    const capturePhoto = async () => {
        if (videoRef.current) {
            const canvas = document.createElement("canvas");
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
            const dataUrl = canvas.toDataURL("image/jpeg");

            stopWebcam();
            setUploadingPhoto(true);

            try {
                const res = await fetch(dataUrl);
                const blob = await res.blob();
                const file = new File([blob], `staff_${Date.now()}.jpg`, { type: "image/jpeg" });
                const uploadedUrl = await uploadImageToAzure(file);
                if (uploadedUrl) {
                    setFormData((prev) => ({ ...prev, userImage: uploadedUrl }));
                    toast.success("Photo captured and saved!");
                } else {
                    setFormData((prev) => ({ ...prev, userImage: dataUrl }));
                }
                if (errors.userImage) setErrors((prev) => ({ ...prev, userImage: null }));
            } catch (err) {
                setFormData((prev) => ({ ...prev, userImage: dataUrl }));
            } finally {
                setUploadingPhoto(false);
            }
        }
    };

    // Document File Upload via Azure Storage with Base64 fallback
    const handleDocumentFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadingDoc(true);
        try {
            const uploadedUrl = await uploadImageToAzure(file);
            if (uploadedUrl) {
                setFormData((prev) => ({
                    ...prev,
                    documentFilePath: uploadedUrl,
                    documentFileName: file.name
                }));
                toast.success("Document attached successfully!");
            } else {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setFormData((prev) => ({
                        ...prev,
                        documentFilePath: reader.result,
                        documentFileName: file.name
                    }));
                    toast.success("Document attached successfully!");
                };
                reader.readAsDataURL(file);
            }
        } catch (err) {
            console.error("Document upload error:", err);
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData((prev) => ({
                    ...prev,
                    documentFilePath: reader.result,
                    documentFileName: file.name
                }));
                toast.success("Document attached successfully!");
            };
            reader.readAsDataURL(file);
        } finally {
            setUploadingDoc(false);
        }
    };

    useEffect(() => {
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
            }
        };
    }, []);

    const genderOptions = [
        { label: "Male", value: "Male" },
        { label: "Female", value: "Female" },
        { label: "Other", value: "Other" }
    ];

    const bloodGroupOptions = [
        { label: "A+", value: "A+" },
        { label: "A-", value: "A-" },
        { label: "B+", value: "B+" },
        { label: "B-", value: "B-" },
        { label: "O+", value: "O+" },
        { label: "O-", value: "O-" },
        { label: "AB+", value: "AB+" },
        { label: "AB-", value: "AB-" }
    ];

    const employmentStatusOptions = [
        { label: "Active", value: "Active" },
        { label: "InActive", value: "InActive" }
    ];

    const documentTypeOptions = [
        { label: "Aadhaar Card", value: "Aadhaar" },
        { label: "PAN Card", value: "PAN" },
        { label: "Voter ID", value: "Voter ID" },
        { label: "Driving License", value: "Driving License" },
        { label: "Passport", value: "Passport" }
    ];

    const validateForm = () => {
        const newErrors = {};

        // Personal Details
        if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
        if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";

        if (!formData.mobileNumber.trim()) {
            newErrors.mobileNumber = "Mobile number is required";
        } else if (!/^[0-9]{10}$/.test(formData.mobileNumber.trim())) {
            newErrors.mobileNumber = "Enter a valid 10-digit mobile number";
        }

        if (!formData.gender) newErrors.gender = "Gender is required";

        if (!formData.dateOfBirth) {
            newErrors.dateOfBirth = "Date of birth is required";
        } else {
            const dob = new Date(formData.dateOfBirth);
            const today = new Date();
            if (dob >= today) {
                newErrors.dateOfBirth = "Date of birth cannot be in the future";
            } else {
                const age = today.getFullYear() - dob.getFullYear();
                if (age < 18) {
                    newErrors.dateOfBirth = "Staff member must be at least 18 years old";
                }
            }
        }

        // Employment Details
        if (staffType === "vendor" && !formData.vendorId) {
            newErrors.vendorId = "Associated Vendor is required";
        }

        if (!formData.staffRoleId) {
            newErrors.staffRoleId = "Staff Role is required";
        }

        if (!formData.joiningDate) {
            newErrors.joiningDate = "Date of joining is required";
        }

        if (formData.dateOfBirth && formData.joiningDate) {
            if (new Date(formData.joiningDate) < new Date(formData.dateOfBirth)) {
                newErrors.joiningDate = "Joining date cannot be before Date of Birth";
            }
        }

        // Vendorless Duty Details
        if (staffType === "vendorless") {
            if (!formData.shiftId) newErrors.shiftId = "Assigned Shift is required";
        }

        // Emergency Contact
        if (formData.emergencyContactNumber && !/^[0-9]{10}$/.test(formData.emergencyContactNumber.trim())) {
            newErrors.emergencyContactNumber = "Emergency contact number must be 10 digits";
        }

        setErrors(newErrors);

        const errorKeys = Object.keys(newErrors);
        if (errorKeys.length > 0) {
            const firstKey = errorKeys[0];
            const firstMessage = newErrors[firstKey];
            toast.warning(`Required field missing: ${firstMessage}`);

            setTimeout(() => {
                const el = document.getElementById(firstKey);
                if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "center" });
                    el.focus();
                }
            }, 100);
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        try {
            let payload = {};

            if (staffType === "vendor") {
                // FLOW A: Vendor Staff Payload
                payload = {
                    vendorId: Number(formData.vendorId),
                    staffRoleId: Number(formData.staffRoleId),
                    firstName: formData.firstName.trim(),
                    lastName: formData.lastName.trim(),
                    mobileNumber: formData.mobileNumber.trim(),
                    gender: formData.gender,
                    dateOfBirth: toIsoDateString(formData.dateOfBirth),
                    bloodGroup: formData.bloodGroup || null,
                    photoUrl: formData.userImage || "",
                    joiningDate: toIsoDateString(formData.joiningDate),
                    emergencyContactName: formData.emergencyContactName?.trim() || null,
                    emergencyContactNumber: formData.emergencyContactNumber?.trim() || null,
                    employmentStatus: formData.employmentStatus || "Active",
                    createdBy: createdBy,
                    modifiedBy: createdBy
                };
            } else {
                // FLOW B: Vendorless / Direct Staff Payload
                payload = {
                    vendorId: null,
                    staffRoleId: Number(formData.staffRoleId),
                    firstName: formData.firstName.trim(),
                    lastName: formData.lastName.trim(),
                    mobileNumber: formData.mobileNumber.trim(),
                    gender: formData.gender,
                    dateOfBirth: toIsoDateString(formData.dateOfBirth),
                    bloodGroup: formData.bloodGroup || null,
                    photoUrl: formData.userImage || "",
                    joiningDate: toIsoDateString(formData.joiningDate),
                    employmentStatus: formData.employmentStatus || "Active",
                    createdBy: createdBy,
                    modifiedBy: createdBy,
                    siteId: Number(assignedSiteId),
                    wingId: assignedWingId ? Number(assignedWingId) : undefined,
                    floorId: assignedFloorId ? Number(assignedFloorId) : undefined,
                    unitId: assignedUnitId ? Number(assignedUnitId) : undefined,
                    shiftId: Number(formData.shiftId),
                    emergencyContactName: formData.emergencyContactName?.trim() || null,
                    emergencyContactNumber: formData.emergencyContactNumber?.trim() || null
                };
            }

            const cleanPayload = Object.fromEntries(
                Object.entries(payload).filter(([_, v]) => v !== undefined && v !== "" && v !== null)
            );

            if (isEditMode) {
                // UPDATE EXISTING STAFF
                cleanPayload.staffId = Number(editStaffId);
                cleanPayload.modifiedBy = createdBy;

                const response = await staffService.updateStaff(editStaffId, cleanPayload);
                if (response.data?.isSuccess || response.status === 200) {
                    toast.success("Staff updated successfully.");

                    // Upload identity document if attached
                    if (formData.documentFilePath) {
                        try {
                            await staffService.uploadStaffDocument({
                                staffId: Number(editStaffId),
                                documentType: formData.documentType,
                                documentNumber: formData.documentNumber ? formData.documentNumber.trim() : "NA",
                                fileName: formData.documentFileName || `${formData.documentType || "Document"}.jpg`,
                                filePath: formData.documentFilePath,
                                createdBy: createdBy
                            });
                            toast.success("Identity document uploaded successfully.");
                        } catch (docErr) {
                            console.error("Document upload failed during staff update:", docErr);
                        }
                    }

                    // Update duty allotment for vendorless staff if shift is configured
                    if (staffType === "vendorless" && formData.shiftId) {
                        try {
                            await staffService.allotDuty({
                                staffId: Number(editStaffId),
                                siteId: Number(assignedSiteId),
                                wingId: assignedWingId ? Number(assignedWingId) : undefined,
                                floorId: assignedFloorId ? Number(assignedFloorId) : undefined,
                                unitId: assignedUnitId ? Number(assignedUnitId) : undefined,
                                shiftId: Number(formData.shiftId),
                                startDate: toIsoDateString(new Date().toISOString().split("T")[0]),
                                assignedBy: createdBy
                            });
                        } catch (allotErr) {
                            console.error("Duty allotment update failed:", allotErr);
                        }
                    }

                    // Redirect back
                    if (staffType === "vendor" && (queryVendorId || formData.vendorId)) {
                        navigate(
                            `/zentres/workforce-management/vendor/staff?vendorId=${formData.vendorId}&vendorName=${encodeURIComponent(
                                formData.vendorName || "Vendor"
                            )}`
                        );
                    } else {
                        navigate("/zentres/workforce-management/staff");
                    }
                } else {
                    toast.error(response.data?.message || "Failed to update staff member.");
                }
            } else {
                // CREATE NEW STAFF
                console.log("CreateStaff Payload:", cleanPayload);
                const response = await staffService.createStaff(cleanPayload);

                if (response.data?.isSuccess) {
                    const createdStaffId =
                        response.data?.id ||
                        response.data?.staffId ||
                        response.data?.data?.id ||
                        response.data?.data?.staffId ||
                        response.data?.data;

                    if (staffType === "vendor") {
                        toast.success("Staff onboarded successfully.");
                    } else {
                        toast.success(
                            "Staff onboarded successfully. Duty allotment and device enrollment have been initiated automatically."
                        );
                    }

                    // Upload identity document if attached
                    if (createdStaffId && formData.documentFilePath) {
                        try {
                            await staffService.uploadStaffDocument({
                                staffId: Number(createdStaffId),
                                documentType: formData.documentType,
                                documentNumber: formData.documentNumber ? formData.documentNumber.trim() : "NA",
                                fileName: formData.documentFileName || `${formData.documentType || "Document"}.jpg`,
                                filePath: formData.documentFilePath,
                                createdBy: createdBy
                            });
                            toast.success("Identity document uploaded successfully.");
                        } catch (docErr) {
                            console.error("Document upload failed post-staff creation:", docErr);
                            toast.warning(
                                "Staff created successfully. Document upload failed. You can upload the document from the staff details page."
                            );
                        }
                    }

                    // Redirect based on context
                    if (staffType === "vendor" && (queryVendorId || formData.vendorId)) {
                        navigate(
                            `/zentres/workforce-management/vendor/staff?vendorId=${formData.vendorId}&vendorName=${encodeURIComponent(
                                formData.vendorName || "Vendor"
                            )}`
                        );
                    } else {
                        navigate("/zentres/workforce-management/staff");
                    }
                } else {
                    toast.error(response.data?.message || "Failed to onboard staff member.");
                }
            }
        } catch (error) {
            console.error("Staff Save API Error:", error);
            const apiMsg =
                error.response?.data?.message ||
                error.response?.data?.title ||
                error.message ||
                "An error occurred while saving staff.";
            toast.error(apiMsg);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <div className="flex items-center justify-between">
                <Breadcrumbs
                    items={[
                        "Workforce Management",
                        staffType === "vendor" ? (queryVendorName ? `${queryVendorName} Staff` : "Vendor Staff") : "Staff",
                        isEditMode ? "Manage Staff" : "Onboard Staff"
                    ]}
                />
            </div>

            <PageHeadingSection
                Navbar={true}
                mainHeading={
                    isEditMode
                        ? `Manage Staff (${formData.firstName} ${formData.lastName})`.trim()
                        : queryVendorName
                            ? `Onboard Staff (${queryVendorName})`
                            : staffType === "vendor"
                                ? "Onboard Vendor Staff"
                                : "Onboard Staff (Vendorless)"
                }
                subHeading={
                    isEditMode
                        ? "View and update staff profile, duty allotment, and identity documents."
                        : staffType === "vendor"
                            ? "Please fill out the form below to onboard staff under this vendor."
                            : "Please fill out the form below to onboard direct facility staff and configure their permissions."
                }
            />

            <div className="flex flex-col gap-8 mt-6">
                {/* ─────────────────────────────────────────────────────────────
                    CARD 1: Personal Information (Photo + Inputs)
                   ───────────────────────────────────────────────────────────── */}
                <div className="bg-sectionheaderbg p-6 rounded-2xl border border-gray-100 dark:border-bordercolor">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                            <FontAwesomeIcon icon={faUser} className="text-sm" />
                        </div>
                        <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary tracking-tight">
                            Personal Information
                        </h3>
                    </div>

                    <div className="flex flex-col md:flex-row gap-6 mb-6">
                        {/* Profile Photo with Upload and Camera Snapshot (Optional) */}
                        <div className="flex-shrink-0 flex flex-col items-center justify-start">
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-2">
                                Profile Photo <span className="text-gray-400 font-normal text-[11px]">(Optional)</span>
                            </label>
                            <div
                                className={`relative group ${showWebcam ? "w-48 h-48 rounded-xl" : "w-24 h-24 rounded-full"
                                    } border-2 border-dashed border-gray-300 bg-gray-50 dark:bg-gray-800 transition-all duration-300 flex items-center justify-center overflow-hidden mb-3`}
                            >
                                {uploadingPhoto ? (
                                    <div className="flex flex-col items-center gap-1">
                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-600 text-xl" />
                                        <span className="text-[10px] text-gray-500">Uploading...</span>
                                    </div>
                                ) : showWebcam ? (
                                    <video
                                        ref={videoRef}
                                        autoPlay
                                        playsInline
                                        className="w-full h-full object-cover transform scale-x-[-1]"
                                    />
                                ) : (
                                    <>
                                        <div className="absolute inset-0 w-full h-full flex flex-col items-center justify-center text-gray-400 transition-colors">
                                            <FontAwesomeIcon icon={faUser} className="text-2xl mb-1 opacity-50" />
                                        </div>
                                        {formData.userImage &&
                                            formData.userImage !== "null" &&
                                            formData.userImage !== "undefined" &&
                                            formData.userImage.trim() !== "" && (
                                                <img
                                                    src={formData.userImage}
                                                    alt="Profile"
                                                    className="absolute inset-0 w-full h-full object-cover z-10 bg-gray-50"
                                                    onError={(e) => {
                                                        e.target.style.display = "none";
                                                    }}
                                                />
                                            )}
                                    </>
                                )}
                            </div>

                            <div className="flex gap-2 w-full justify-center">
                                {!showWebcam && (
                                    <div className="relative">
                                        <button
                                            type="button"
                                            disabled={uploadingPhoto}
                                            className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md text-[10px] font-semibold hover:bg-blue-100 transition-colors flex items-center gap-1"
                                        >
                                            <FontAwesomeIcon icon={faUpload} /> Upload
                                        </button>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        />
                                    </div>
                                )}
                                {showWebcam ? (
                                    <>
                                        <button
                                            type="button"
                                            onClick={capturePhoto}
                                            className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-[10px] font-semibold hover:bg-blue-700 transition-colors flex items-center gap-1"
                                        >
                                            <FontAwesomeIcon icon={faCamera} /> Snap
                                        </button>
                                        <button
                                            type="button"
                                            onClick={stopWebcam}
                                            className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-md text-[10px] font-semibold hover:bg-gray-200 transition-colors flex items-center gap-1"
                                        >
                                            <FontAwesomeIcon icon={faTimes} /> Cancel
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={startWebcam}
                                        disabled={uploadingPhoto}
                                        className="px-3 py-1.5 bg-emerald-50 text-emerald-600 rounded-md text-[10px] font-semibold hover:bg-emerald-100 transition-colors flex items-center gap-1"
                                    >
                                        <FontAwesomeIcon icon={faCamera} /> Capture
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* First Name & Last Name */}
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className={`block text-xs font-bold mb-1 transition-colors ${errors.firstName ? "text-red-500 font-bold" : "text-gray-700 dark:text-textsecondary"}`} htmlFor="firstName">
                                    First Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 transition-all ${
                                        errors.firstName 
                                            ? "border-red-500 bg-red-50/10 focus:ring-red-500/20 focus:border-red-500" 
                                            : "border-gray-200 dark:border-bordercolor hover:border-gray-400 focus:ring-blue-500/20 focus:border-blue-500"
                                    }`}
                                    type="text"
                                    id="firstName"
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    placeholder="Enter first name"
                                />
                                {errors.firstName && <p className="text-red-500 text-[10px] mt-1 font-medium">{errors.firstName}</p>}
                            </div>
                            <div>
                                <label className={`block text-xs font-bold mb-1 transition-colors ${errors.lastName ? "text-red-500 font-bold" : "text-gray-700 dark:text-textsecondary"}`} htmlFor="lastName">
                                    Last Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 transition-all ${
                                        errors.lastName 
                                            ? "border-red-500 bg-red-50/10 focus:ring-red-500/20 focus:border-red-500" 
                                            : "border-gray-200 dark:border-bordercolor hover:border-gray-400 focus:ring-blue-500/20 focus:border-blue-500"
                                    }`}
                                    type="text"
                                    id="lastName"
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    placeholder="Enter last name"
                                />
                                {errors.lastName && <p className="text-red-500 text-[10px] mt-1 font-medium">{errors.lastName}</p>}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div>
                            <label className={`block text-xs font-bold mb-1 transition-colors ${errors.mobileNumber ? "text-red-500 font-bold" : "text-gray-700 dark:text-textsecondary"}`} htmlFor="mobileNumber">
                                Mobile Number <span className="text-red-500">*</span>
                            </label>
                            <input
                                className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 transition-all ${
                                    errors.mobileNumber 
                                        ? "border-red-500 bg-red-50/10 focus:ring-red-500/20 focus:border-red-500" 
                                        : "border-gray-200 dark:border-bordercolor hover:border-gray-400 focus:ring-blue-500/20 focus:border-blue-500"
                                }`}
                                type="text"
                                id="mobileNumber"
                                value={formData.mobileNumber}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                                    setFormData((prev) => ({ ...prev, mobileNumber: val }));
                                    if (errors.mobileNumber) setErrors((prev) => ({ ...prev, mobileNumber: null }));
                                }}
                                maxLength="10"
                                placeholder="10-digit mobile number"
                            />
                            {errors.mobileNumber && <p className="text-red-500 text-[10px] mt-1 font-medium">{errors.mobileNumber}</p>}
                        </div>

                        <div>
                            <label className={`block text-xs font-bold mb-1 transition-colors ${errors.dateOfBirth ? "text-red-500 font-bold" : "text-gray-700 dark:text-textsecondary"}`} htmlFor="dateOfBirth">
                                Date of Birth <span className="text-red-500">*</span>
                            </label>
                            <input
                                className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 transition-all ${
                                    errors.dateOfBirth 
                                        ? "border-red-500 bg-red-50/10 focus:ring-red-500/20 focus:border-red-500" 
                                        : "border-gray-200 dark:border-bordercolor hover:border-gray-400 focus:ring-blue-500/20 focus:border-blue-500"
                                }`}
                                type="date"
                                id="dateOfBirth"
                                max={new Date().toISOString().split("T")[0]}
                                value={formData.dateOfBirth}
                                onChange={handleInputChange}
                            />
                            {errors.dateOfBirth && <p className="text-red-500 text-[10px] mt-1 font-medium">{errors.dateOfBirth}</p>}
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="age">
                                Age
                            </label>
                            <input
                                className="w-full px-3 py-2 border rounded-md text-sm bg-gray-50 dark:bg-gray-800 text-textprimary border-gray-200 dark:border-bordercolor cursor-not-allowed"
                                type="number"
                                id="age"
                                value={formData.age}
                                readOnly
                                placeholder="Auto-calculated from DOB"
                            />
                        </div>

                        <div>
                            <CustomSelect
                                id="gender"
                                label="Gender"
                                required={true}
                                options={genderOptions}
                                value={formData.gender ? genderOptions.find((g) => g.value === formData.gender) : null}
                                onChange={(opt) => {
                                    setFormData((prev) => ({ ...prev, gender: opt?.value || "" }));
                                    if (errors.gender) setErrors((prev) => ({ ...prev, gender: null }));
                                }}
                                placeholder="Select Gender"
                                error={errors.gender}
                                errorMessage={errors.gender}
                            />
                        </div>

                        <div>
                            <CustomSelect
                                id="bloodGroup"
                                label="Blood Group"
                                options={bloodGroupOptions}
                                value={formData.bloodGroup ? bloodGroupOptions.find((b) => b.value === formData.bloodGroup) : null}
                                onChange={(opt) => setFormData((prev) => ({ ...prev, bloodGroup: opt?.value || "" }))}
                                placeholder="Select Blood Group"
                            />
                        </div>
                    </div>
                </div>

                {/* ─────────────────────────────────────────────────────────────
                    CARD 2: Employment Details (Vendor vs Vendorless)
                   ───────────────────────────────────────────────────────────── */}
                <div className="bg-sectionheaderbg p-6 rounded-2xl border border-gray-100 dark:border-bordercolor">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                            <FontAwesomeIcon icon={faBriefcase} className="text-sm" />
                        </div>
                        <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary tracking-tight">
                            {staffType === "vendor" ? "Vendor Employment Details" : "Employment & Duty Details"}
                        </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Flow A: Associated Vendor */}
                        {staffType === "vendor" && (
                            <div>
                                {queryVendorId ? (
                                    <div>
                                        <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                            Associated Vendor <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            readOnly
                                            value={queryVendorName || `Vendor #${queryVendorId}`}
                                            className="w-full px-3 py-2 border rounded-md text-sm bg-gray-100 dark:bg-gray-800 dark:text-textprimary border-gray-200 dark:border-bordercolor cursor-not-allowed font-medium"
                                        />
                                    </div>
                                ) : (
                                    <CustomSelect
                                        id="vendorId"
                                        label="Associated Vendor"
                                        required={true}
                                        options={vendors}
                                        value={vendors.find((v) => v.value === String(formData.vendorId)) || null}
                                        onChange={(opt) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                vendorId: opt?.value || "",
                                                vendorName: opt?.label || ""
                                            }));
                                            if (errors.vendorId) setErrors((prev) => ({ ...prev, vendorId: null }));
                                        }}
                                        placeholder={loadingVendors ? "Loading vendors..." : "Select Vendor"}
                                        error={errors.vendorId}
                                        errorMessage={errors.vendorId}
                                    />
                                )}
                            </div>
                        )}

                        {/* Flow B: Location Scope (Read-only / Contextual) */}
                        {staffType === "vendorless" && (
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                    Assigned Unit / Scope
                                </label>
                                <input
                                    type="text"
                                    readOnly
                                    value={`Site: ${assignedSiteId} | Wing: ${assignedWingId} | Floor: ${assignedFloorId} | Unit: ${assignedUnitId}`}
                                    className="w-full px-3 py-2 border rounded-md text-sm bg-gray-100 dark:bg-gray-800 dark:text-textprimary border-gray-200 dark:border-bordercolor cursor-not-allowed font-semibold text-emerald-600 dark:text-emerald-400"
                                />
                            </div>
                        )}

                        {/* Dynamic Staff Role */}
                        <div>
                            <CustomSelect
                                id="staffRoleId"
                                label="Staff Role"
                                required={true}
                                options={roles}
                                value={roles.find((r) => r.value === String(formData.staffRoleId)) || null}
                                onChange={(opt) => {
                                    setFormData((prev) => ({ ...prev, staffRoleId: opt?.value || "" }));
                                    if (errors.staffRoleId) setErrors((prev) => ({ ...prev, staffRoleId: null }));
                                }}
                                placeholder={loadingRoles ? "Loading roles..." : "Select Staff Role"}
                                error={errors.staffRoleId}
                                errorMessage={errors.staffRoleId}
                            />
                        </div>

                        {/* Flow B: Assigned Shift */}
                        {staffType === "vendorless" && (
                            <div>
                                <CustomSelect
                                    id="shiftId"
                                    label="Assigned Shift"
                                    required={true}
                                    options={shifts}
                                    value={shifts.find((s) => s.value === String(formData.shiftId)) || null}
                                    onChange={(opt) => {
                                        setFormData((prev) => ({ ...prev, shiftId: opt?.value || "" }));
                                        if (errors.shiftId) setErrors((prev) => ({ ...prev, shiftId: null }));
                                    }}
                                    placeholder={loadingShifts ? "Loading shifts..." : "Select Shift"}
                                    error={errors.shiftId}
                                    errorMessage={errors.shiftId}
                                />
                            </div>
                        )}

                        {/* Date of Joining */}
                        <div>
                            <label className={`block text-xs font-bold mb-1 transition-colors ${errors.joiningDate ? "text-red-500 font-bold" : "text-gray-700 dark:text-textsecondary"}`} htmlFor="joiningDate">
                                Date of Joining <span className="text-red-500">*</span>
                            </label>
                            <input
                                className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 transition-all ${
                                    errors.joiningDate 
                                        ? "border-red-500 bg-red-50/10 focus:ring-red-500/20 focus:border-red-500" 
                                        : "border-gray-200 dark:border-bordercolor hover:border-gray-400 focus:ring-emerald-500/20 focus:border-emerald-500"
                                }`}
                                type="date"
                                id="joiningDate"
                                value={formData.joiningDate}
                                onChange={handleInputChange}
                            />
                            {errors.joiningDate && <p className="text-red-500 text-[10px] mt-1 font-medium">{errors.joiningDate}</p>}
                        </div>

                        {/* Employment Status */}
                        <div>
                            <CustomSelect
                                id="employmentStatus"
                                label="Employment Status"
                                required={true}
                                options={employmentStatusOptions}
                                value={employmentStatusOptions.find((s) => s.value === formData.employmentStatus) || null}
                                onChange={(opt) => setFormData((prev) => ({ ...prev, employmentStatus: opt?.value || "Active" }))}
                                placeholder="Select Status"
                            />
                        </div>
                    </div>
                </div>

                {/* ─────────────────────────────────────────────────────────────
                    CARD 3: Emergency & Identity Document Info
                   ───────────────────────────────────────────────────────────── */}
                <div className="bg-sectionheaderbg p-6 rounded-2xl border border-gray-100 dark:border-bordercolor mb-4">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-8 h-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center">
                            <FontAwesomeIcon icon={faShieldHeart} className="text-sm" />
                        </div>
                        <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary tracking-tight">
                            Emergency & Identity Documents
                        </h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="emergencyContactName">
                                Emergency Contact Name
                            </label>
                            <input
                                className="w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all border-gray-200 dark:border-bordercolor hover:border-gray-400"
                                type="text"
                                id="emergencyContactName"
                                value={formData.emergencyContactName}
                                onChange={handleInputChange}
                                placeholder="Contact person name"
                            />
                        </div>

                        <div>
                            <label className={`block text-xs font-bold mb-1 transition-colors ${errors.emergencyContactNumber ? "text-red-500 font-bold" : "text-gray-700 dark:text-textsecondary"}`} htmlFor="emergencyContactNumber">
                                Emergency Contact Number
                            </label>
                            <input
                                className={`w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 transition-all ${
                                    errors.emergencyContactNumber 
                                        ? "border-red-500 bg-red-50/10 focus:ring-red-500/20 focus:border-red-500" 
                                        : "border-gray-200 dark:border-bordercolor hover:border-gray-400 focus:ring-rose-500/20 focus:border-rose-500"
                                }`}
                                type="text"
                                id="emergencyContactNumber"
                                maxLength="10"
                                value={formData.emergencyContactNumber}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                                    setFormData((prev) => ({ ...prev, emergencyContactNumber: val }));
                                    if (errors.emergencyContactNumber) setErrors((prev) => ({ ...prev, emergencyContactNumber: null }));
                                }}
                                placeholder="10-digit emergency mobile"
                            />
                            {errors.emergencyContactNumber && <p className="text-red-500 text-[10px] mt-1 font-medium">{errors.emergencyContactNumber}</p>}
                        </div>

                        <div>
                            <CustomSelect
                                id="documentType"
                                label="Document Type"
                                options={documentTypeOptions}
                                value={
                                    documentTypeOptions.find((d) => 
                                        d.value.toLowerCase() === (formData.documentType || "").toLowerCase() ||
                                        d.label.toLowerCase() === (formData.documentType || "").toLowerCase() ||
                                        (formData.documentType && (
                                            formData.documentType.toLowerCase().includes(d.value.toLowerCase()) ||
                                            d.label.toLowerCase().includes(formData.documentType.toLowerCase())
                                        ))
                                    ) || (formData.documentType ? { label: formData.documentType, value: formData.documentType } : null)
                                }
                                onChange={(opt) => setFormData((prev) => ({ ...prev, documentType: opt?.value || "Aadhaar" }))}
                                placeholder="Select Document Type"
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="documentNumber">
                                Document / ID Number
                            </label>
                            <input
                                className="w-full px-3 py-2 border rounded-md text-sm bg-transparent text-textprimary focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all border-gray-200 dark:border-bordercolor hover:border-gray-400"
                                type="text"
                                id="documentNumber"
                                value={formData.documentNumber}
                                onChange={handleInputChange}
                                placeholder="e.g. 1234 5678 9012"
                            />
                        </div>

                        <div className="lg:col-span-2">
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                Upload Document File (PDF / Image)
                            </label>
                            <div className="border border-dashed border-gray-300 dark:border-bordercolor rounded-lg px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors relative">
                                {uploadingDoc ? (
                                    <div className="flex items-center gap-2 text-xs text-blue-600">
                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                        <span>Uploading document to storage...</span>
                                    </div>
                                ) : formData.documentFileName ? (
                                    <div className="flex items-center gap-2">
                                        <FontAwesomeIcon icon={faFileAlt} className="text-blue-600 text-lg" />
                                        <span className="text-xs font-semibold text-gray-800 dark:text-textprimary truncate max-w-[240px]">
                                            {formData.documentFileName}
                                        </span>
                                        <span className="text-[10px] text-emerald-600 font-bold ml-2">Attached</span>
                                        <button
                                            type="button"
                                            onClick={() => setFormData((prev) => ({ ...prev, documentFilePath: "", documentFileName: "" }))}
                                            className="p-1 hover:bg-red-50 text-red-500 rounded text-xs ml-2"
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <FontAwesomeIcon icon={faUpload} className="text-gray-400" />
                                        <span>Click to select document file (PDF, JPG, PNG)</span>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept=".pdf,image/*"
                                    onChange={handleDocumentFileUpload}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                />
                            </div>
                        </div>
                    </div>

                    {staffType === "vendorless" && (
                        <div className="mt-5 p-3 bg-blue-50/60 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg text-xs text-blue-800 dark:text-blue-300 flex items-start gap-2">
                            <FontAwesomeIcon icon={faInfoCircle} className="mt-0.5" />
                            <span>
                                <b>Backend Auto-Orchestration:</b> For vendorless staff, the backend automatically performs site/unit duty allotment and enrolls the staff into the Dahua device queue for door access.
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* ─────────────────────────────────────────────────────────────
                FOOTER ACTIONS (Identical to UserManagementForm)
               ───────────────────────────────────────────────────────────── */}
            <div className="flex justify-between mt-auto border-t dark:border-bordercolor pt-4">
                <button
                    type="button"
                    onClick={() => {
                        if (staffType === "vendor" && (queryVendorId || formData.vendorId)) {
                            navigate(
                                `/zentres/workforce-management/vendor/staff?vendorId=${queryVendorId || formData.vendorId}&vendorName=${encodeURIComponent(
                                    queryVendorName || formData.vendorName || "Vendor"
                                )}`
                            );
                        } else {
                            navigate("/zentres/workforce-management/staff");
                        }
                    }}
                    className="px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-xs flex items-center gap-2 text-gray-600 dark:text-textsecondary hover:bg-gray-50 dark:hover:bg-actionitembg transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>

                <div className="flex gap-3">
                    <button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                        style={{ backgroundColor: "var(--color-secondaryaccent)", color: "var(--color-primaryaccent)" }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "var(--color-primaryaccent)";
                            e.currentTarget.style.color = "#FFFFFF";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "var(--color-secondaryaccent)";
                            e.currentTarget.style.color = "var(--color-primaryaccent)";
                        }}
                    >
                        {isLoading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                {isEditMode ? "Updating Staff..." : "Saving Staff..."}
                            </>
                        ) : (
                            <>
                                {isEditMode ? "Update Staff" : "Save Staff"}
                                <FontAwesomeIcon icon={faAngleRight} />
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StaffForm;
